import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase/config.js';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { format, parseISO, isValid as isValidDate, startOfWeek, endOfWeek, isWithinInterval, startOfDay, endOfDay, differenceInBusinessDays, areIntervalsOverlapping, isBefore, isAfter, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, eachDayOfInterval } from 'date-fns';

// Try to import recharts, but handle gracefully if not available
let BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend;
let RECHARTS_AVAILABLE = true;

try {
  const recharts = require('recharts');
  ({ BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } = recharts);
} catch (error) {
  console.warn('Recharts not available. Install with: npm install recharts');
  RECHARTS_AVAILABLE = false;
}

const Analytics = ({ currentUser }) => { 
  // State for Subbing Analytics
  const [subAnalytics, setSubAnalytics] = useState([]);
  const [unfilled, setUnfilled] = useState([]);
  
  // State for Absence Analytics
  const [absenceStats, setAbsenceStats] = useState({ byDept: {}, byEmployee: {} });
  const [visibleAbsences, setVisibleAbsences] = useState([]);
  const [userMap, setUserMap] = useState(new Map());

  // State for the report view
  const [reportStartDate, setReportStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reportEndDate, setReportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // CHANGED: Default view is now 'overview' instead of 'tally'
  const [view, setView] = useState('overview');
  const [displayMode, setDisplayMode] = useState('charts');
  const [sortBy, setSortBy] = useState('subbed');
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  
  // NEW: State for unfilled periods filtering
  const [showAllUnfilled, setShowAllUnfilled] = useState(false);
  
  // NEW: Modal state for sub request details
  const [selectedSubRequest, setSelectedSubRequest] = useState(null);
  const [subRequestDetails, setSubRequestDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // NEW: Calendar state variables
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarData, setCalendarData] = useState(new Map());
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);

  // NEW: Date range activity report
  const [activityStartDate, setActivityStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activityEndDate, setActivityEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activityReport, setActivityReport] = useState(null);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

  // Recharts installation notice component
  const RechartsNotice = () => (
    <div className="notice-card">
      <div className="notice-icon">📊</div>
      <h3>Charts Not Available</h3>
      <p>To view beautiful charts and graphs, please install the recharts library:</p>
      <code className="install-code">npm install recharts</code>
      <p className="notice-footer">After installation, refresh the page to see the charts.</p>
    </div>
  );

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    if (!currentUser?.tenantId) {
      setFetchError("User or school information is not available.");
      setIsLoading(false);
      return;
    }
    try {
      const usersQuery = query(collection(db, 'faculty_staff'), where("tenantId", "==", currentUser.tenantId));
      const usersSnap = await getDocs(usersQuery);
      const allUsers = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const newUserMap = new Map(allUsers.map(user => [user.uid, user]));
      setUserMap(newUserMap);

      const subRequestsQuery = query(collection(db, 'sub_requests'), where("tenantId", "==", currentUser.tenantId));
      const subRequestSnap = await getDocs(subRequestsQuery);
      
      const absenceQuery = query(collection(db, 'absence_requests'), where("tenantId", "==", currentUser.tenantId), where("status", "==", "approved"));
      const absenceSnap = await getDocs(absenceQuery);
      let allAbsences = absenceSnap.docs.map(doc => doc.data());

      // --- Process Sub Request Analytics ---
      const tally = {};
      const unfilledEntries = [];
      subRequestSnap.forEach(doc => {
        const data = doc.data();
        if (!data) return;
        const { name, date: dateString, department, periods, filled = {}, room } = data;
        if (name) {
          if (!tally[name]) tally[name] = { requested: 0, subbed: 0 };
          tally[name].requested = (tally[name].requested || 0) + 1;
        }
        if (Array.isArray(periods)) {
          periods.forEach(period => {
            const subInfo = filled[period];
            if (subInfo && typeof subInfo === 'object' && subInfo.name) {
              const subName = subInfo.name;
              if (!tally[subName]) tally[subName] = { requested: 0, subbed: 0 };
              tally[subName].subbed = (tally[subName].subbed || 0) + 1;
            } else {
              unfilledEntries.push({ name: name || "Unknown", date: dateString, department: department || "N/A", period, room: room || "N/A", originalDateString: typeof dateString === 'string' ? dateString : null });
            }
          });
        }
      });
      const sortedTally = Object.entries(tally).map(([teacher, { requested, subbed }]) => ({ teacher, requested: requested || 0, subbed: subbed || 0 })).filter(e => e.teacher && e.teacher !== 'null');
      const sortedUnfilled = unfilledEntries.sort((a, b) => (parseISO(a.date) || 0) - (parseISO(b.date) || 0));
      setSubAnalytics(sortedTally);
      setUnfilled(sortedUnfilled);

      // --- Filter and Process Absence Analytics ---
      let filteredVisibleAbsences = [];
      if (currentUser.isSystemAdmin || currentUser.role === 'HR') {
          filteredVisibleAbsences = allAbsences;
      } else if (currentUser.isEducationalAdmin) {
          filteredVisibleAbsences = allAbsences.filter(absence => newUserMap.get(absence.requesterUid)?.role === 'Faculty/Staff');
      } else if (currentUser.isSupervisor) {
          const reportUids = allUsers.filter(u => u.supervisorId === currentUser.docId).map(u => u.uid);
          filteredVisibleAbsences = allAbsences.filter(absence => reportUids.includes(absence.requesterUid));
      }
      setVisibleAbsences(filteredVisibleAbsences);

      const statsByDept = {};
      const statsByEmployee = {};
      
      filteredVisibleAbsences.forEach(absence => {
          const user = newUserMap.get(absence.requesterUid);
          const dept = user?.department || 'Unknown Department';
          const employeeName = absence.requesterName;

          let days = 1;
          if (absence.startDate && absence.endDate) {
              const start = parseISO(absence.startDate);
              const end = parseISO(absence.endDate);
              if (isValidDate(start) && isValidDate(end)) {
                  days = differenceInBusinessDays(end, start) + 1;
              }
          }
          
          const reason = absence.reason === 'Other' ? (absence.reasonOther || 'Other') : absence.reason;

          if (!statsByDept[dept]) statsByDept[dept] = { total: 0, byReason: {} };
          statsByDept[dept].total += days;
          statsByDept[dept].byReason[reason] = (statsByDept[dept].byReason[reason] || 0) + days;

          if (!statsByEmployee[employeeName]) statsByEmployee[employeeName] = { total: 0, byReason: {}, department: dept };
          statsByEmployee[employeeName].total += days;
          statsByEmployee[employeeName].byReason[reason] = (statsByEmployee[employeeName].byReason[reason] || 0) + days;
      });
      setAbsenceStats({ byDept: statsByDept, byEmployee: statsByEmployee });

    } catch (error) {
      console.error("Analytics: Error in fetchAnalytics:", error);
      setFetchError(error.message || "Failed to fetch analytics data.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.tenantId) { fetchAnalytics(); }
    else if (currentUser) {
      setFetchError("Your user profile is missing school information (tenantId).");
      setIsLoading(false);
    }
  }, [fetchAnalytics, currentUser]);

  // NEW: Load Calendar Data
  const loadCalendarData = async (monthDate) => {
    setIsLoadingCalendar(true);
    try {
      const startDate = startOfMonth(monthDate);
      const endDate = endOfMonth(monthDate);

      const subRequestsQuery = query(
        collection(db, 'sub_requests'),
        where('tenantId', '==', currentUser.tenantId)
      );
      
      const requestsSnapshot = await getDocs(subRequestsQuery);
      const dataByDate = new Map();
      
      requestsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        let requestDate;
        
        if (data.date?.toDate) {
          requestDate = data.date.toDate();
        } else if (typeof data.date === 'string') {
          requestDate = parseISO(data.date);
        }
        
        if (!requestDate || !isValidDate(requestDate)) return;
        if (isBefore(requestDate, startDate) || isAfter(requestDate, endDate)) return;
        
        const dateKey = format(requestDate, 'yyyy-MM-dd');
        
        if (!dataByDate.has(dateKey)) {
          dataByDate.set(dateKey, {
            date: requestDate,
            dateStr: dateKey,
            requests: []
          });
        }
        
        const periods = data.periods || [];
        periods.forEach(period => {
          const periodInfo = {
            requestId: doc.id,
            teacher: data.name || 'Unknown',
            department: data.department || 'N/A',
            period: period,
            reason: data.reason || '',
            room: data.room || '',
            note: data.periodNotes?.[period] || '',
            filled: data.filled?.[period] || null,
            status: data.filled?.[period] ? 'Filled' : 'Unfilled'
          };
          
          dataByDate.get(dateKey).requests.push(periodInfo);
        });
      });
      
      setCalendarData(dataByDate);
    } catch (error) {
      console.error('Error loading calendar data:', error);
      alert('Error loading calendar data: ' + error.message);
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  // Load calendar data when view changes to calendar or month changes
  useEffect(() => {
    if (view === 'calendar' && currentUser?.tenantId) {
      loadCalendarData(currentMonth);
    }
  }, [view, currentMonth, currentUser?.tenantId]);

  // NEW: Load Activity Report for Date Range
  const loadActivityReport = async () => {
    if (!activityStartDate || !activityEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    const start = parseISO(activityStartDate);
    const end = parseISO(activityEndDate);

    if (!isValidDate(start) || !isValidDate(end)) {
      alert('Invalid date range');
      return;
    }

    if (isAfter(start, end)) {
      alert('Start date must be before or equal to end date');
      return;
    }

    setIsLoadingActivity(true);
    try {
      // Get all sub requests
      const subRequestsQuery = query(
        collection(db, 'sub_requests'),
        where('tenantId', '==', currentUser.tenantId)
      );
      const requestsSnapshot = await getDocs(subRequestsQuery);

      // Get all absence requests
      const absenceQuery = query(
        collection(db, 'absence_requests'),
        where('tenantId', '==', currentUser.tenantId),
        where('status', '==', 'approved')
      );
      const absenceSnapshot = await getDocs(absenceQuery);

      // Process activities by date
      const activitiesByDate = {};
      
      // Process sub requests
      requestsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        let requestDate;
        
        if (data.date?.toDate) {
          requestDate = data.date.toDate();
        } else if (typeof data.date === 'string') {
          requestDate = parseISO(data.date);
        }
        
        if (!requestDate || !isValidDate(requestDate)) return;
        
        // Check if date is within range
        if (isWithinInterval(requestDate, { start, end })) {
          const dateKey = format(requestDate, 'yyyy-MM-dd');
          
          if (!activitiesByDate[dateKey]) {
            activitiesByDate[dateKey] = {
              date: requestDate,
              dateStr: dateKey,
              absences: [],
              subRequests: []
            };
          }

          const periods = data.periods || [];
          periods.forEach(period => {
            const subInfo = {
              teacher: data.name || 'Unknown',
              department: data.department || 'N/A',
              period: period,
              reason: data.reason || '',
              room: data.room || '',
              note: data.periodNotes?.[period] || '',
              filled: data.filled?.[period] || null,
              sub: data.filled?.[period]?.name || null
            };
            
            activitiesByDate[dateKey].subRequests.push(subInfo);
          });
        }
      });

      // Process absences
      absenceSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.startDate || !data.endDate) return;

        const absenceStart = parseISO(data.startDate);
        const absenceEnd = parseISO(data.endDate);

        if (!isValidDate(absenceStart) || !isValidDate(absenceEnd)) return;

        // Get all dates in the absence period that fall within our range
        const absenceDates = eachDayOfInterval({ start: absenceStart, end: absenceEnd });
        
        absenceDates.forEach(absenceDate => {
          if (isWithinInterval(absenceDate, { start, end })) {
            const dateKey = format(absenceDate, 'yyyy-MM-dd');
            
            if (!activitiesByDate[dateKey]) {
              activitiesByDate[dateKey] = {
                date: absenceDate,
                dateStr: dateKey,
                absences: [],
                subRequests: []
              };
            }

            activitiesByDate[dateKey].absences.push({
              name: data.requesterName || 'Unknown',
              department: userMap.get(data.requesterUid)?.department || 'N/A',
              reason: data.reason === 'Other' ? (data.reasonOther || 'Other') : data.reason,
              startDate: data.startDate,
              endDate: data.endDate
            });
          }
        });
      });

      // Convert to sorted array
      const sortedActivities = Object.values(activitiesByDate).sort((a, b) => 
        a.date.getTime() - b.date.getTime()
      );

      // Calculate summary statistics
      const summary = {
        totalDays: sortedActivities.length,
        totalAbsences: sortedActivities.reduce((sum, day) => sum + day.absences.length, 0),
        totalSubRequests: sortedActivities.reduce((sum, day) => sum + day.subRequests.length, 0),
        filledSubs: sortedActivities.reduce((sum, day) => 
          sum + day.subRequests.filter(req => req.filled).length, 0
        ),
        unfilledSubs: sortedActivities.reduce((sum, day) => 
          sum + day.subRequests.filter(req => !req.filled).length, 0
        )
      };

      setActivityReport({
        activities: sortedActivities,
        summary,
        startDate: activityStartDate,
        endDate: activityEndDate
      });

    } catch (error) {
      console.error('Error loading activity report:', error);
      alert('Error loading activity report: ' + error.message);
    } finally {
      setIsLoadingActivity(false);
    }
  };

  // NEW: Calendar helper functions
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const dateKey = format(date, 'yyyy-MM-dd');
    if (calendarData.has(dateKey)) {
      setSelectedDate(date);
    }
  };

  const getDateData = (date) => {
    if (!date) return null;
    const dateKey = format(date, 'yyyy-MM-dd');
    return calendarData.get(dateKey);
  };

  const isToday = (date) => {
    if (!date) return false;
    return isSameDay(date, new Date());
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return isSameDay(date, selectedDate);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDate(null);
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(null);
  };

  // Sorting logic
  const sortedAnalytics = [...subAnalytics].sort((a, b) => {
    if (sortBy === 'subbed') return b.subbed - a.subbed;
    if (sortBy === 'requested') return b.requested - a.requested;
    if (sortBy === 'name') return a.teacher.localeCompare(b.teacher);
    return 0;
  });

  // Filter unfilled (future or past)
  const displayedUnfilled = showAllUnfilled
    ? unfilled
    : unfilled.filter(entry => {
        if (!entry.date) return true;
        let dateVal;
        if (typeof entry.date === 'string') dateVal = parseISO(entry.date);
        else if (entry.date.toDate) dateVal = entry.date.toDate();
        else dateVal = entry.date;
        return isAfter(dateVal, new Date()) || isSameDay(dateVal, new Date());
      });

  // Chart data: absences by department
  const absenceChartData = Object.entries(absenceStats.byDept).map(([dept, { total }]) => ({ name: dept, absences: total }));

  // Report rendering: daily
  const renderReport = () => {
    const startDate = parseISO(reportStartDate);
    const endDate = parseISO(reportEndDate);
    if (!isValidDate(startDate) || !isValidDate(endDate)) return <p>Invalid date range.</p>;
    const datesInRange = eachDayOfInterval({ start: startDate, end: endDate });
    const dailyStats = datesInRange.map(dayDate => {
      const dayKey = format(dayDate, 'yyyy-MM-dd');
      const dayAbsences = visibleAbsences.filter(absence => {
        if (!absence.startDate || !absence.endDate) return false;
        const absStart = parseISO(absence.startDate);
        const absEnd = parseISO(absence.endDate);
        if (!isValidDate(absStart) || !isValidDate(absEnd)) return false;
        return isWithinInterval(dayDate, { start: absStart, end: absEnd });
      });
      const daySubRequests = subAnalytics.map(teacher => {
        const user = Array.from(userMap.values()).find(u => u.name === teacher.teacher);
        const theirRequests = unfilled.filter(u => u.name === teacher.teacher);
        const matchDay = theirRequests.filter(req => {
          if (!req.date) return false;
          let rDate;
          if (typeof req.date === 'string') rDate = parseISO(req.date);
          else if (req.date.toDate) rDate = req.date.toDate();
          else rDate = req.date;
          return isValidDate(rDate) && isSameDay(rDate, dayDate);
        });
        return matchDay.length;
      }).reduce((sum, val) => sum + val, 0);
      return { date: dayKey, absences: dayAbsences.length, subRequests: daySubRequests };
    });
    return (
      <div>
        <h3 style={{ color: '#17a2b8', marginBottom: '20px' }}>Daily Report</h3>
        {dailyStats.map(day => (
          <div key={day.date} style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>{format(parseISO(day.date), 'EEEE, MMMM d, yyyy')}</div>
            <div style={{ fontSize: '0.9rem', color: '#374151' }}>Absences: <strong>{day.absences}</strong> | Sub Requests: <strong>{day.subRequests}</strong></div>
          </div>
        ))}
      </div>
    );
  };

  // Additional UI sections for different analytics
  const renderDeptChart = () => {
    if (!RECHARTS_AVAILABLE) return <RechartsNotice />;
    if (absenceChartData.length === 0) return <p>No absence data available.</p>;
    return (
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={absenceChartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-30} textAnchor="end" height={100} />
          <YAxis />
          <Tooltip />
          <Bar dataKey="absences" fill="#17a2b8" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderEmployeeChart = () => {
    if (!RECHARTS_AVAILABLE) return <RechartsNotice />;
    const topEmployees = Object.entries(absenceStats.byEmployee)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 10)
      .map(([name, stats]) => ({ name, total: stats.total }));
    if (topEmployees.length === 0) return <p>No employee data available.</p>;
    return (
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={topEmployees}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-30} textAnchor="end" height={100} />
          <YAxis />
          <Tooltip />
          <Bar dataKey="total" fill="#f59e0b" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (isLoading && !userMap.size) {
    return <div style={{ padding: '40px', textAlign: 'center', fontSize: '1.2rem', color: '#6b7280' }}>Loading analytics...</div>;
  }

  if (fetchError) {
    return <div style={{ padding: '40px', textAlign: 'center', fontSize: '1.2rem', color: '#ef4444' }}>Error: {fetchError}</div>;
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>📊 Analytics Dashboard</h1>
        <p>Comprehensive view of substitute and absence patterns</p>
      </div>

      {/* View Selector */}
      <div className="view-selector">
        <button 
          className={`view-btn ${view === 'overview' ? 'active' : ''}`}
          onClick={() => setView('overview')}
        >
          📈 Overview
        </button>
        <button 
          className={`view-btn ${view === 'tally' ? 'active' : ''}`}
          onClick={() => setView('tally')}
        >
          📋 Sub Tally
        </button>
        <button 
          className={`view-btn ${view === 'unfilled' ? 'active' : ''}`}
          onClick={() => setView('unfilled')}
        >
          ⚠️ Unfilled Periods
        </button>
        <button 
          className={`view-btn ${view === 'report' ? 'active' : ''}`}
          onClick={() => setView('report')}
        >
          📅 Daily Report
        </button>
        <button 
          className={`view-btn ${view === 'calendar' ? 'active' : ''}`}
          onClick={() => setView('calendar')}
        >
          📆 Calendar
        </button>
      </div>

      {/* OVERVIEW View */}
      {view === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">👥</div>
              <div className="metric-value">{subAnalytics.length}</div>
              <div className="metric-label">Active Teachers</div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">✅</div>
              <div className="metric-value">{subAnalytics.reduce((sum, t) => sum + t.subbed, 0)}</div>
              <div className="metric-label">Periods Covered</div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">📝</div>
              <div className="metric-value">{subAnalytics.reduce((sum, t) => sum + t.requested, 0)}</div>
              <div className="metric-label">Total Requests</div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">⚠️</div>
              <div className="metric-value">{unfilled.length}</div>
              <div className="metric-label">Unfilled Periods</div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Absences by Department</h3>
              {renderDeptChart()}
            </div>
            <div className="chart-card">
              <h3>Top 10 Employees by Absence Days</h3>
              {renderEmployeeChart()}
            </div>
          </div>
        </>
      )}

      {/* TALLY View */}
      {view === 'tally' && (
        <div className="content-card">
          <div className="controls-card">
            <div className="control-group">
              <label>Display Mode</label>
              <select value={displayMode} onChange={(e) => setDisplayMode(e.target.value)} className="control-select">
                <option value="charts">Charts</option>
                <option value="table">Table</option>
              </select>
            </div>
            <div className="control-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="control-select">
                <option value="subbed">Most Periods Subbed</option>
                <option value="requested">Most Requests Made</option>
                <option value="name">Teacher Name</option>
              </select>
            </div>
          </div>

          {displayMode === 'charts' ? (
            <div className="charts-grid">
              <div className="chart-card">
                <h3>Periods Subbed</h3>
                {RECHARTS_AVAILABLE ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={sortedAnalytics}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="teacher" angle={-45} textAnchor="end" height={120} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="subbed" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <RechartsNotice />
                )}
              </div>
              <div className="chart-card">
                <h3>Requests Made</h3>
                {RECHARTS_AVAILABLE ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={sortedAnalytics}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="teacher" angle={-45} textAnchor="end" height={120} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="requested" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <RechartsNotice />
                )}
              </div>
            </div>
          ) : (
            <div className="table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Teacher</th>
                    <th>Requests Made</th>
                    <th>Periods Subbed</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAnalytics.map((entry, idx) => (
                    <tr key={idx}>
                      <td>{entry.teacher}</td>
                      <td>{entry.requested}</td>
                      <td>{entry.subbed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* UNFILLED View */}
      {view === 'unfilled' && (
        <div className="content-card">
          <div className="unfilled-header">
            <h2>Unfilled Periods</h2>
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={showAllUnfilled}
                onChange={(e) => setShowAllUnfilled(e.target.checked)}
              />
              Show All (including past dates)
            </label>
          </div>
          {displayedUnfilled.length === 0 ? (
            <div className="no-unfilled">
              <p>✅ All periods are currently filled!</p>
            </div>
          ) : (
            <div className="unfilled-list">
              {displayedUnfilled.map((entry, idx) => (
                <div key={idx} className="unfilled-item">
                  <div className="unfilled-details">
                    <div><strong>{entry.name}</strong> ({entry.department})</div>
                    <div>{entry.date ? format(typeof entry.date === 'string' ? parseISO(entry.date) : (entry.date.toDate ? entry.date.toDate() : entry.date), 'EEEE, MMMM d, yyyy') : 'Unknown Date'}</div>
                    <div>Period {entry.period} | Room: {entry.room}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* REPORT View */}
      {view === 'report' && (
        <div className="content-card">
          <h2>Daily Activity Report</h2>
          <div className="date-controls">
            <div className="date-inputs">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
            </div>
            <div className="date-buttons">
              <button onClick={() => { setReportStartDate(format(startOfWeek(new Date()), 'yyyy-MM-dd')); setReportEndDate(format(endOfWeek(new Date()), 'yyyy-MM-dd')); }} className="quick-date-btn">This Week</button>
              <button onClick={() => { setReportStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd')); setReportEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd')); }} className="quick-date-btn">This Month</button>
            </div>
          </div>
          {renderReport()}
        </div>
      )}

      {/* CALENDAR View */}
      {view === 'calendar' && (
        <div className="content-card">
          <h2>Calendar View</h2>
          
          {/* NEW: Date Range Activity Report Section */}
          <div className="activity-report-section">
            <h3>📋 Activity Report by Date Range</h3>
            <p>Select a date range to view all absences, sub requests, and who covered them during that period.</p>
            
            <div className="activity-date-controls">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={activityStartDate}
                  onChange={(e) => setActivityStartDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={activityEndDate}
                  onChange={(e) => setActivityEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <button 
                onClick={loadActivityReport}
                disabled={isLoadingActivity}
                className="generate-report-btn"
              >
                {isLoadingActivity ? 'Loading...' : 'Generate Report'}
              </button>
            </div>

            {activityReport && (
              <div className="activity-report-results">
                <div className="report-summary">
                  <h4>Summary for {format(parseISO(activityReport.startDate), 'MMM d, yyyy')} - {format(parseISO(activityReport.endDate), 'MMM d, yyyy')}</h4>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="stat-label">Days with Activity:</span>
                      <span className="stat-value">{activityReport.summary.totalDays}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Total Absences:</span>
                      <span className="stat-value">{activityReport.summary.totalAbsences}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Total Sub Requests:</span>
                      <span className="stat-value">{activityReport.summary.totalSubRequests}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Filled:</span>
                      <span className="stat-value success">{activityReport.summary.filledSubs}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Unfilled:</span>
                      <span className="stat-value warning">{activityReport.summary.unfilledSubs}</span>
                    </div>
                  </div>
                </div>

                <div className="daily-activities">
                  {activityReport.activities.map((dayActivity) => (
                    <div key={dayActivity.dateStr} className="day-activity-card">
                      <div className="day-header">
                        <h5>{format(dayActivity.date, 'EEEE, MMMM d, yyyy')}</h5>
                        <div className="day-counts">
                          <span className="count-badge absence-badge">{dayActivity.absences.length} Absent</span>
                          <span className="count-badge request-badge">{dayActivity.subRequests.length} Requests</span>
                        </div>
                      </div>

                      {dayActivity.absences.length > 0 && (
                        <div className="activity-section">
                          <h6>🏥 Absences</h6>
                          <div className="activity-list">
                            {dayActivity.absences.map((absence, idx) => (
                              <div key={idx} className="activity-item">
                                <strong>{absence.name}</strong> ({absence.department})
                                <span className="reason-badge">{absence.reason}</span>
                                {absence.startDate !== absence.endDate && (
                                  <span className="date-range">
                                    {format(parseISO(absence.startDate), 'MMM d')} - {format(parseISO(absence.endDate), 'MMM d')}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {dayActivity.subRequests.length > 0 && (
                        <div className="activity-section">
                          <h6>📝 Sub Requests</h6>
                          <div className="activity-list">
                            {dayActivity.subRequests.map((request, idx) => (
                              <div key={idx} className="activity-item">
                                <div className="request-header">
                                  <strong>{request.teacher}</strong> ({request.department})
                                  <span className="period-badge">Period {request.period}</span>
                                  {request.room && <span className="room-info">Room: {request.room}</span>}
                                </div>
                                {request.filled ? (
                                  <div className="sub-info filled">
                                    ✅ Covered by: <strong>{request.sub}</strong>
                                  </div>
                                ) : (
                                  <div className="sub-info unfilled">
                                    ⚠️ Not yet filled
                                  </div>
                                )}
                                {request.note && <div className="request-note">Note: {request.note}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Existing Calendar Grid */}
          <div className="calendar-section">
            <div className="calendar-controls">
              <button onClick={handlePrevMonth} className="calendar-nav-btn">
                ← Previous
              </button>
              <div className="calendar-month-display">
                <h3>{format(currentMonth, 'MMMM yyyy')}</h3>
                <button onClick={handleToday} className="today-btn">
                  Today
                </button>
              </div>
              <button onClick={handleNextMonth} className="calendar-nav-btn">
                Next →
              </button>
            </div>

            {isLoadingCalendar ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading calendar...</div>
            ) : (
              <>
                <div className="calendar-grid">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="calendar-day-header">{day}</div>
                  ))}
                  
                  {getDaysInMonth().map((date, index) => {
                    if (!date) {
                      return <div key={`empty-${index}`} className="calendar-day empty" />;
                    }

                    const dateData = getDateData(date);
                    const hasData = dateData && dateData.requests.length > 0;
                    const filledCount = dateData ? dateData.requests.filter(r => r.filled).length : 0;
                    const unfilledCount = dateData ? dateData.requests.filter(r => !r.filled).length : 0;

                    return (
                      <div
                        key={index}
                        className={`calendar-day ${isToday(date) ? 'today' : ''} ${isSelected(date) ? 'selected' : ''} ${hasData ? 'has-data' : ''}`}
                        onClick={() => handleDateClick(date)}
                      >
                        <div className="calendar-date-number">{format(date, 'd')}</div>
                        {hasData && (
                          <div className="calendar-indicators">
                            {filledCount > 0 && (
                              <span className="indicator-dot filled">{filledCount} ✓</span>
                            )}
                            {unfilledCount > 0 && (
                              <span className="indicator-dot unfilled">{unfilledCount} !</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedDate && getDateData(selectedDate) && (
                  <div className="date-details">
                    <h3>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</h3>
                    <div className="date-stats">
                      <span>Total Requests: <strong>{getDateData(selectedDate).requests.length}</strong></span>
                      <span className="stat-filled">
                        Filled: <strong>{getDateData(selectedDate).requests.filter(r => r.filled).length}</strong>
                      </span>
                      <span className="stat-unfilled">
                        Unfilled: <strong>{getDateData(selectedDate).requests.filter(r => !r.filled).length}</strong>
                      </span>
                    </div>
                    
                    <div className="table-container">
                      <table className="modern-table">
                        <thead>
                          <tr>
                            <th>Teacher</th>
                            <th>Department</th>
                            <th>Period</th>
                            <th>Room</th>
                            <th>Status</th>
                            <th>Substitute</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getDateData(selectedDate).requests.map((request, idx) => (
                            <tr key={idx}>
                              <td>{request.teacher}</td>
                              <td>{request.department}</td>
                              <td>Period {request.period}</td>
                              <td>{request.room}</td>
                              <td>
                                <span className={`status-badge ${request.filled ? 'status-filled' : 'status-unfilled'}`}>
                                  {request.filled ? '✅ Filled' : '⚠️ Unfilled'}
                                </span>
                              </td>
                              <td>{request.filled ? request.filled.name : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .analytics-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 30px;
          background: #f8fafc;
          min-height: 100vh;
        }

        .analytics-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 40px;
          border-radius: 12px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .analytics-header h1 {
          margin: 0 0 10px 0;
          font-size: 2.5rem;
          font-weight: 700;
        }

        .analytics-header p {
          margin: 0;
          opacity: 0.95;
          font-size: 1.1rem;
        }

        .view-selector {
          display: flex;
          gap: 15px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .view-btn {
          padding: 12px 24px;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
          color: #374151;
        }

        .view-btn:hover {
          border-color: #17a2b8;
          color: #17a2b8;
          transform: translateY(-2px);
        }

        .view-btn.active {
          background: #17a2b8;
          color: white;
          border-color: #17a2b8;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .metric-card {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: transform 0.2s;
        }

        .metric-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }

        .metric-icon {
          font-size: 2.5rem;
          margin-bottom: 15px;
        }

        .metric-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #17a2b8;
          margin-bottom: 10px;
        }

        .metric-label {
          font-size: 1rem;
          color: #6b7280;
          font-weight: 500;
        }

        .content-card {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .controls-card {
          display: flex;
          gap: 30px;
          margin-bottom: 30px;
          padding: 25px;
          background: #f9fafb;
          border-radius: 8px;
          flex-wrap: wrap;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .control-group label {
          font-weight: 600;
          color: #374151;
          font-size: 0.95rem;
        }

        .control-select {
          padding: 10px 15px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 1rem;
          min-width: 200px;
          background: white;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .control-select:focus {
          outline: none;
          border-color: #17a2b8;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 30px;
          margin-top: 30px;
        }

        .chart-card {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .chart-card h3 {
          margin: 0 0 25px 0;
          color: #1f2937;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .table-container {
          overflow-x: auto;
          margin-top: 20px;
        }

        .modern-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
        }

        .modern-table thead {
          background: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
        }

        .modern-table th {
          padding: 15px 20px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .modern-table td {
          padding: 15px 20px;
          border-bottom: 1px solid #f3f4f6;
          color: #1f2937;
        }

        .modern-table tbody tr:hover {
          background: #f9fafb;
        }

        .status-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-filled {
          background: #d1fae5;
          color: #065f46;
        }

        .status-unfilled {
          background: #fee2e2;
          color: #991b1b;
        }

        .unfilled-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          flex-wrap: wrap;
          gap: 15px;
        }

        .unfilled-header h2 {
          margin: 0;
          color: #1f2937;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-weight: 500;
          color: #374151;
        }

        .toggle-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .unfilled-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: 20px;
        }

        .unfilled-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          transition: all 0.2s;
        }

        .unfilled-item:hover {
          border-color: #17a2b8;
          box-shadow: 0 2px 8px rgba(23, 162, 184, 0.2);
        }

        .unfilled-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
          color: #374151;
        }

        .no-unfilled {
          text-align: center;
          padding: 60px 20px;
          background: #f9fafb;
          border-radius: 8px;
          margin-top: 20px;
          border: 1px solid #e5e7eb;
        }

        .no-unfilled p {
          margin: 0;
          color: #10b981;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .date-controls {
          display: flex;
          gap: 20px;
          align-items: flex-end;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .date-inputs {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .date-input-group label {
          font-weight: 600;
          color: #374151;
          font-size: 0.95rem;
        }

        .date-input {
          padding: 10px 15px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 1rem;
          background: white;
          min-width: 180px;
        }

        .date-input:focus {
          outline: none;
          border-color: #17a2b8;
        }

        .date-buttons {
          display: flex;
          gap: 10px;
        }

        .quick-date-btn {
          padding: 10px 20px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          color: #374151;
        }

        .quick-date-btn:hover {
          border-color: #17a2b8;
          color: #17a2b8;
        }

        .notice-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 40px;
          border-radius: 12px;
          text-align: center;
        }

        .notice-icon {
          font-size: 4rem;
          margin-bottom: 20px;
        }

        .notice-card h3 {
          margin: 0 0 15px 0;
          font-size: 1.8rem;
        }

        .notice-card p {
          margin: 10px 0;
          opacity: 0.95;
        }

        .install-code {
          display: block;
          background: rgba(255, 255, 255, 0.2);
          padding: 15px 25px;
          border-radius: 8px;
          margin: 20px 0;
          font-family: 'Courier New', monospace;
          font-size: 1.1rem;
        }

        .notice-footer {
          margin-top: 20px;
          font-size: 0.9rem;
          opacity: 0.9;
        }

        /* Calendar styles */
        .calendar-section {
          margin-top: 30px;
        }

        .calendar-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          gap: 20px;
          flex-wrap: wrap;
        }

        .calendar-nav-btn {
          padding: 10px 20px;
          background: #17a2b8;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .calendar-nav-btn:hover {
          background: #138496;
        }

        .calendar-month-display {
          text-align: center;
        }

        .calendar-month-display h3 {
          margin: 0 0 10px 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .today-btn {
          padding: 6px 16px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          color: #374151;
        }

        .today-btn:hover {
          border-color: #17a2b8;
          color: #17a2b8;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
          background: #d1d5db;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 30px;
        }

        .calendar-day-header {
          background: #f9fafb;
          padding: 12px;
          text-align: center;
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .calendar-day {
          background: white;
          min-height: 90px;
          padding: 10px;
          position: relative;
          transition: background 0.2s;
        }

        .calendar-day.empty {
          background: #f9fafb;
          cursor: default;
        }

        .calendar-day.today {
          background: #e0f2fe;
          border: 2px solid #0ea5e9;
        }

        .calendar-day.selected {
          background: #d1fae5;
          border: 2px solid #10b981;
        }

        .calendar-day.has-data {
          cursor: pointer;
        }

        .calendar-day.has-data:hover {
          background: #f3f4f6;
        }

        .calendar-date-number {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
          font-size: 1rem;
        }

        .calendar-indicators {
          display: flex;
          gap: 6px;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
        }

        .indicator-dot {
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .indicator-dot.filled {
          background: #d1fae5;
          color: #065f46;
        }

        .indicator-dot.unfilled {
          background: #fee2e2;
          color: #991b1b;
        }

        .date-details {
          margin-top: 30px;
          padding: 30px;
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .date-details h3 {
          margin: 0 0 15px 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .date-stats {
          display: flex;
          gap: 20px;
          margin-bottom: 25px;
          font-size: 0.95rem;
          color: #374151;
          padding: 15px;
          background: #f9fafb;
          border-radius: 6px;
        }

        .stat-filled {
          color: #10b981;
          font-weight: 600;
        }

        .stat-unfilled {
          color: #ef4444;
          font-weight: 600;
        }

        /* NEW: Activity Report Styles */
        .activity-report-section {
          margin-bottom: 40px;
          padding: 30px;
          background: #f0f9ff;
          border-radius: 12px;
          border: 2px solid #0ea5e9;
        }

        .activity-report-section h3 {
          margin: 0 0 10px 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .activity-report-section > p {
          margin: 0 0 20px 0;
          color: #6b7280;
        }

        .activity-date-controls {
          display: flex;
          gap: 15px;
          align-items: flex-end;
          flex-wrap: wrap;
          margin-bottom: 25px;
        }

        .generate-report-btn {
          padding: 12px 30px;
          background: #0ea5e9;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          font-size: 1rem;
        }

        .generate-report-btn:hover:not(:disabled) {
          background: #0284c7;
        }

        .generate-report-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .activity-report-results {
          margin-top: 25px;
        }

        .report-summary {
          background: white;
          padding: 25px;
          border-radius: 8px;
          margin-bottom: 25px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .report-summary h4 {
          margin: 0 0 20px 0;
          color: #1f2937;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .summary-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 20px;
        }

        .summary-stat {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1f2937;
        }

        .stat-value.success {
          color: #10b981;
        }

        .stat-value.warning {
          color: #f59e0b;
        }

        .daily-activities {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .day-activity-card {
          background: white;
          border-radius: 8px;
          padding: 25px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border-left: 4px solid #0ea5e9;
        }

        .day-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f3f4f6;
          flex-wrap: wrap;
          gap: 10px;
        }

        .day-header h5 {
          margin: 0;
          color: #1f2937;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .day-counts {
          display: flex;
          gap: 10px;
        }

        .count-badge {
          padding: 6px 12px;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .absence-badge {
          background: #fee2e2;
          color: #991b1b;
        }

        .request-badge {
          background: #dbeafe;
          color: #1e40af;
        }

        .activity-section {
          margin-bottom: 20px;
        }

        .activity-section:last-child {
          margin-bottom: 0;
        }

        .activity-section h6 {
          margin: 0 0 15px 0;
          color: #374151;
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .activity-item {
          padding: 15px;
          background: #f9fafb;
          border-radius: 6px;
          border-left: 3px solid #d1d5db;
        }

        .activity-item strong {
          color: #1f2937;
        }

        .reason-badge {
          display: inline-block;
          margin-left: 10px;
          padding: 4px 10px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .date-range {
          display: inline-block;
          margin-left: 10px;
          padding: 4px 10px;
          background: #fef3c7;
          color: #92400e;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .request-header {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }

        .period-badge {
          padding: 4px 10px;
          background: #dbeafe;
          color: #1e3a8a;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .room-info {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .sub-info {
          padding: 8px 12px;
          border-radius: 6px;
          margin-top: 8px;
          font-size: 0.9rem;
        }

        .sub-info.filled {
          background: #d1fae5;
          color: #065f46;
        }

        .sub-info.unfilled {
          background: #fee2e2;
          color: #991b1b;
        }

        .request-note {
          margin-top: 8px;
          padding: 8px;
          background: #fef3c7;
          border-left: 3px solid #fbbf24;
          font-size: 0.875rem;
          color: #78350f;
          border-radius: 4px;
        }

        @media (max-width: 768px) {
          .analytics-container {
            padding: 15px;
          }

          .analytics-header {
            padding: 20px;
          }

          .analytics-header h1 {
            font-size: 2rem;
          }

          .controls-card {
            flex-direction: column;
            align-items: stretch;
          }

          .control-group {
            width: 100%;
          }

          .control-select {
            min-width: auto;
            width: 100%;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .charts-grid {
            grid-template-columns: 1fr;
          }

          .date-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .date-buttons {
            justify-content: center;
          }

          .unfilled-details {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .modern-table {
            font-size: 0.85rem;
          }

          .modern-table th,
          .modern-table td {
            padding: 10px 15px;
          }

          .calendar-grid {
            gap: 0;
          }

          .calendar-day {
            min-height: 80px;
            padding: 5px;
          }

          .calendar-date-number {
            font-size: 0.9rem;
          }

          .calendar-controls {
            flex-direction: column;
            gap: 15px;
          }

          .activity-date-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .date-input-group {
            width: 100%;
          }

          .date-input {
            width: 100%;
            min-width: auto;
          }

          .generate-report-btn {
            width: 100%;
          }

          .summary-stats {
            grid-template-columns: 1fr;
          }

          .day-header {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default Analytics;